import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';

import css from './ShareButton.module.css';

/**
 * Share this page.
 *
 * Two behaviours, one button, chosen by what the device can actually do:
 *   - navigator.share where it exists (every mobile browser worth naming) —
 *     the OS sheet is what people expect and it reaches WhatsApp, which is how
 *     a Malaysian design team actually passes a link around.
 *   - copy-to-clipboard everywhere else, with a "Copied" confirmation, because
 *     a share button that silently does nothing is worse than no button.
 *
 * The capability check runs in an effect rather than at render: navigator does
 * not exist during SSR, and branching on it while rendering would make the
 * server and the first client render disagree (hydration mismatch). Until the
 * effect runs the button reads "Share", which is true either way.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.url] - defaults to the current location
 * @param {string} [props.title] - share sheet title
 * @param {string} [props.text] - share sheet body
 * @param {Function} [props.onShare] - fired once per successful share/copy
 *   (this is where the caller records its engagement event)
 * @param {string} [props.className]
 * @param {boolean} [props.iconOnly] - render the glyph without the label
 */
const ShareButton = props => {
  const { url, title, text, onShare, className, iconOnly = false } = props;

  const [canUseSheet, setCanUseSheet] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);

  useEffect(() => {
    setCanUseSheet(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
    return () => window.clearTimeout(copiedTimer.current);
  }, []);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const flashCopied = () => {
    setCopied(true);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(false), 2200);
  };

  const copyToClipboard = () => {
    if (navigator?.clipboard?.writeText) {
      return navigator.clipboard.writeText(shareUrl);
    }
    // Older Safari / non-secure contexts: the deprecated path is the only
    // path, and it beats a button that does nothing.
    const el = document.createElement('textarea');
    el.value = shareUrl;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
    } finally {
      document.body.removeChild(el);
    }
    return Promise.resolve();
  };

  const handleClick = () => {
    if (canUseSheet) {
      navigator
        .share({ title, text, url: shareUrl })
        // A share is only reported when it actually happened — the promise
        // rejects when the sheet is dismissed, and a cancelled share is not a
        // share.
        .then(() => onShare && onShare('native'))
        .catch(() => {});
      return;
    }
    copyToClipboard()
      .then(() => {
        flashCopied();
        onShare && onShare('copy');
      })
      .catch(() => {});
  };

  return (
    <button
      type="button"
      className={classNames(css.root, { [css.iconOnly]: iconOnly }, className)}
      onClick={handleClick}
    >
      <svg
        className={css.icon}
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" />
        <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
      </svg>
      {!iconOnly ? (
        <span className={css.label}>
          <FormattedMessage id={copied ? 'ShareButton.copied' : 'ShareButton.share'} />
        </span>
      ) : null}
      {iconOnly && copied ? (
        <span className={css.copiedPill}>
          <FormattedMessage id="ShareButton.copied" />
        </span>
      ) : null}
    </button>
  );
};

export default ShareButton;
